import { graphql } from '~/graphql';
import type { OrderStatus } from '~/graphql/graphql';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';

export const useReceiveOrder = () => {
  const receiveOrderMutation = graphql(`
    mutation receiveOrder($orderId: ID!) {
      receiveOrder(id: $orderId) {
        id
      }
    }
  `);

  return useGraphqlMutation(receiveOrderMutation, ['orders']);
};

export const useGetOrdersCountByStatus = () => {
  const orderCountsQuery = graphql(`
    query getOrderCounts {
      ordersCount {
        confirmed
        delivered
        draft
        pendingSellerConfirmation
        shipped
      }
    }
  `);

  return useGraphqlQuery(orderCountsQuery, ['orderCounts']);
};

export const useShipOrder = () => {
  const shipOrderMutation = graphql(`
    mutation ShipOrder(
      $orderId: ID!
      $shippedDate: Instant!
      $deliveryStartDate: Instant!
      $deliveryEndDate: Instant
    ) {
      shipOrder(
        id: $orderId
        shippedDate: $shippedDate
        deliveryStartDate: $deliveryStartDate
        deliveryEndDate: $deliveryEndDate
      ) {
        id
      }
    }
  `);

  return useGraphqlMutation(shipOrderMutation, ['orders']);
};

export const useGetOrderById = (orderId: string) => {
  const query = graphql(`
    query GetOrderById($orderId: ID!) {
      order(id: $orderId) {
        id
        createdAt
        status
        shippedDate
        deliveryStartDate
        deliveryEndDate
        receivedDate
        buyer {
          firstName
          lastName
        }
        shippingLocation {
          name
          addressLine1
          addressLine2
          city
          region
          zipCode
          organization {
            name
          }
        }
        sellingOrganization {
          name
        }
        items {
          quantity {
            value
            unit
          }
          price
          platformProductsLengths {
            id
            platformProduct {
              id
              name
              actualWidthInches
              actualThicknessInches
              nominalWidthInches
              nominalThicknessInches
              properties {
                grade {
                  code
                  name
                }
              }
            }
            length {
              id
              displayValue
              actualLengthInches
              nominalLengthInches
            }
          }
        }
      }
    }
  `);

  return useGraphqlQuery(query, ['orders', orderId], {
    orderId: orderId,
  });
};

export const useGetOrders = (status?: OrderStatus | undefined) => {
  const ordersQuery = graphql(`
    query getOrders($status: OrderStatus) {
      orders(status: $status) {
        createdAt
        id
        buyingOrganization {
          name
        }
        sellingOrganization {
          name
        }
        items {
          quantity {
            value
            unit
          }
          price
        }
        status
      }
    }
  `);

  return useGraphqlQuery(
    ordersQuery,
    ['orders', status ?? ''],
    status ? { status } : undefined
  );
};

export const useConfirmOrder = () => {
  const confirmOrderMutation = graphql(`
    mutation confirmOrder($orderId: ID!) {
      confirmOrder(id: $orderId) {
        id
      }
    }
  `);

  return useGraphqlMutation(confirmOrderMutation, ['orders']);
};

export const usePlaceOrder = () => {
  const placeOrderMutation = graphql(`
    mutation placeOrder($orderId: ID!, $locationId: ID!) {
      placeOrder(orderId: $orderId, locationId: $locationId) {
        id
      }
    }
  `);

  return useGraphqlMutation(placeOrderMutation, ['orders']);
};
